import { useState } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import {
  List,
  useListContext,
  useDataProvider,
  Loading,
  Error,
} from "react-admin";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import SaveIcon from "@mui/icons-material/Save";
import { arrayMoveImmutable } from "array-move";
import isEqual from "lodash/isEqual";

import resources from "../../constants/resources";
import { HEADER_ID } from "../../constants/contentConstants";
import {
  HeaderField,
  IdField,
  ListItemField,
  NameField,
  PreviewField,
  PreviewHeaderField,
} from "../../styles/orderableList";
import { TopicActions } from "./TopicUtilsComponents";
import { SAVE_TOPIC_ORDER_LABEL } from "../../constants/titleConstants";
import { AddAndOrderButtonStyles } from "../../styles/commonStyles";
import DragHandle from "../common/DragHandle";

const HeaderItem = () => {
  const headerData = {
    [HEADER_ID]: {
      id: "Id",
      name_en_us: "Name",
      description_en_us: "Description",
    },
  };
  return (
    <ListItemField>
      <HeaderField record={headerData[HEADER_ID]} source="id" flexbasis="5" />
      <HeaderField
        record={headerData[HEADER_ID]}
        source="name_en_us"
        flexbasis="13"
      />
      <PreviewHeaderField
        record={headerData[HEADER_ID]}
        source="description_en_us"
      />
    </ListItemField>
  );
};

const SortableItem = SortableElement(({ currentId, data }) => (
  <ListItemField>
    <IdField record={data[currentId]} source="id" />
    <NameField record={data[currentId]} source="name_en" />
    <PreviewField record={data[currentId]} source="description_en" />
    <DragHandle />
  </ListItemField>
));

const SortableListContainer = SortableContainer(({ ...props }) => (
  <List
    basePath={resources.TOPIC.url}
    resource={resources.TOPIC.url}
    perPage={500}
    pagination={false}
    actions={
      <TopicActions
        topicOrderableHandle={props.topicOrderableHandle}
        isShowingTopicOrderable={props.isShowingTopicOrderable}
      />
    }
    sort={{ field: "order", order: "ASC" }}
  >
    <SortableGrid setItems={props.setItems} items={props.items} {...props} />
  </List>
));

const SortableGrid = ({ ...props }) => {
  const { data = [] } = useListContext();
  const topicData = data.reduce((accumulator, item) => {
    accumulator[item.id] = item;
    return accumulator;
  }, {});
  const ids = data.map(item => item.id);
  const { items, setItems } = props;
  if (!isEqual([...[HEADER_ID].concat(ids)].sort(), [...items].sort())) {
    const orderSortedIds = Object.values(data)
      .sort((a, b) => a.order - b.order)
      .map(content => content.id);
    setItems([HEADER_ID].concat(orderSortedIds));
  }
  return (
    <div style={{ margin: "1em" }}>
      {items.map((id, index) => {
        if (id === HEADER_ID) {
          return <HeaderItem />;
        }
        return (
          <SortableItem
            key={id}
            currentId={id}
            data={topicData}
            index={index}
            value={id}
            {...props}
          />
        );
      })}
    </div>
  );
};

const SortableList = ({ ...props }) => {
  const { topicOrderableHandle } = props;
  const [items, setItems] = useState([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dataProvider = useDataProvider();

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(prevItems => arrayMoveImmutable(prevItems, oldIndex, newIndex));
    setHasOrderChanged(true);
  };

  const handleSaveTopicsOrder = () => {
    setLoading(true);
    const topics = items.length > 0 ? items.slice(1) : items;
    const topicIdObjs = topics.map(topic => ({
      id: topic,
    }));
    dataProvider
      .create(resources.TOPICS_ORDERABLE.url, {
        data: topicIdObjs,
      })
      .then(() => {
        setLoading(false);
        setHasOrderChanged(false);
        topicOrderableHandle();
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  };

  return (
    <>
      <SortableListContainer
        {...props}
        onSortEnd={onSortEnd}
        useDragHandle
        lockAxis="y"
        items={items}
        setItems={setItems}
      />
      {error && <Error error={error} />}
      {loading ? (
        <Loading />
      ) : (
        <AddAndOrderButtonStyles
          variant="contained"
          onClick={handleSaveTopicsOrder}
          title={SAVE_TOPIC_ORDER_LABEL}
          disabled={!hasOrderChanged}
          startIcon={<SaveIcon />}
        >
          {SAVE_TOPIC_ORDER_LABEL}
        </AddAndOrderButtonStyles>
      )}
    </>
  );
};

const TopicOrderableList = props => (
  <Card raised>
    <CardContent>
      <SortableList {...props} />
    </CardContent>
  </Card>
);

export default TopicOrderableList;
